import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, Legend, LineChart, Line, ComposedChart,
} from "recharts";
import { Link } from "react-router-dom";
import { fmtNumber, fmtPct } from "../lib/utils";
import { useAuth } from "../lib/auth";
import MyKpisWidget from "../components/MyKpisWidget";
import {
  TrendingDown, TrendingUp, AlertTriangle, ShieldCheck, FileBarChart2, Gauge, Sparkles, ChevronRight,
} from "lucide-react";

const ISSUE_COLORS = {
  "Carbon Emissions": "#064E3B",
  "Energy Consumption": "#047857",
  "Waste & E-waste": "#10B981",
  "Talent": "#3B82F6",
  "Health & Well-being": "#6366F1",
  "Data Privacy & Security": "#F59E0B",
  "AI Workforce Transformation": "#EF4444",
  "Living Wage": "#0EA5E9",
  "Community": "#A855F7",
};

function SummaryCard({ label, value, sub, icon, accent, testid }) {
  const Icon = icon;
  return (
    <div className="card p-4 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="section-label">{label}</div>
        <div className={"h-7 w-7 rounded-md flex items-center justify-center text-white " + (accent || "bg-brand")}>
          <Icon size={14} />
        </div>
      </div>
      <div className="metric-value">{value}</div>
      <div className="text-xs text-ink-soft">{sub}</div>
    </div>
  );
}

function MaterialIssueCard({ issue, rows }) {
  const tracked = rows.length;
  const anomalies = rows.reduce((s, r) => s + r.open_anomalies, 0);
  const improvingCount = rows.filter(r => r.improving).length;
  const avgConf = rows.reduce((s,r)=>s+r.confidence,0)/Math.max(tracked,1);
  // ratio of improving as a tiny gauge
  const ratio = improvingCount / Math.max(tracked, 1);
  const color = ISSUE_COLORS[issue] || "#064E3B";
  return (
    <Link
      to={`/scorecard?issue=${encodeURIComponent(issue)}`}
      className="card p-4 hover:shadow-card hover:-translate-y-0.5 transition block"
      data-testid={`issue-card-${issue.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="dot" style={{ background: color }} />
          <div className="font-semibold text-sm">{issue}</div>
        </div>
        <ChevronRight size={14} className="text-ink-mute" />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-ink-soft">KPIs</div>
          <div className="font-mono text-lg">{tracked}</div>
        </div>
        <div>
          <div className="text-ink-soft">Improving</div>
          <div className="font-mono text-lg">{improvingCount}/{tracked}</div>
        </div>
        <div>
          <div className="text-ink-soft">Anomalies</div>
          <div className={"font-mono text-lg " + (anomalies > 0 ? "text-status-error" : "text-ink")}>{anomalies}</div>
        </div>
      </div>
      <div className="mt-3 h-1.5 bg-surface rounded-full overflow-hidden">
        <div style={{ width: `${ratio*100}%`, background: color }} className="h-full" />
      </div>
      <div className="mt-1 text-[11px] text-ink-soft">Confidence avg <span className="font-mono">{Math.round(avgConf*100)}%</span></div>
    </Link>
  );
}

function MiniSpark({ data, color = "#064E3B" }) {
  return (
    <ResponsiveContainer width="100%" height={40}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={`g-${color.replace('#','')}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25}/>
            <stop offset="100%" stopColor={color} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <Area dataKey="v" stroke={color} strokeWidth={1.5} fill={`url(#g-${color.replace('#','')})`} dot={false}/>
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/scorecard").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Failed"));
  }, []);

  if (err) return <div className="p-6 text-status-error" data-testid="dashboard-error">{err}</div>;
  if (!data) return <div className="p-6 text-ink-soft" data-testid="dashboard-loading">Loading dashboard…</div>;

  const { summary, rows, emissions_trend, tenant, consolidation } = data;

  // group rows by material issue
  const byIssue = {};
  rows.forEach(r => {
    const k = r.kpi.material_issue;
    (byIssue[k] = byIssue[k] || []).push(r);
  });

  // featured KPI rows (top 5 anomalies + improving leaders)
  const featured = [...rows]
    .sort((a,b) => b.open_anomalies - a.open_anomalies || Math.abs(b.qoq_change_pct) - Math.abs(a.qoq_change_pct))
    .slice(0, 6);

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-root">
      {/* Title */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label">Executive Dashboard</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">
            {tenant.name} <span className="text-ink-soft font-normal">· {tenant.country}</span>
          </h1>
          <div className="text-sm text-ink-soft">
            Period {rows[0]?.current_period || "—"} · {tenant.headcount.toLocaleString()} FTE · Baseline {tenant.baseline_year}
          </div>
          {consolidation && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="dashboard-consolidation">
              <span className="pill pill-info text-[10px]">
                Consolidation: {consolidation.approach.replaceAll("_", " ")} · factor {(consolidation.factor * 100).toFixed(1)}%
              </span>
              <span className="text-[10px] text-ink-soft">
                {consolidation.company_count} {consolidation.company_count === 1 ? "company" : "companies"}
                {" · "}{consolidation.fully_consolidated} full · {consolidation.partial} partial · {consolidation.excluded} excluded
              </span>
              <a href="/admin/entities" className="text-[10px] text-brand font-semibold hover:underline">
                Manage →
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Personalised "My KPIs" tray (shows only when user owns at least one KPI) */}
      <MyKpisWidget userRole={user?.role}/>

      {/* Top KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard label="KPIs tracked" value={summary.kpis_tracked} sub="across 8 material issues"
          icon={Gauge} accent="bg-brand" testid="kpi-tile-tracked" />
        <SummaryCard label="Avg confidence"
          value={`${Math.round(summary.avg_confidence*100)}%`} sub="across approved measurements"
          icon={ShieldCheck} accent="bg-brand-700" testid="kpi-tile-confidence" />
        <SummaryCard label="On-track KPIs"
          value={`${summary.on_track}/${summary.kpis_tracked}`}
          sub="vs baseline → target glidepath" icon={TrendingDown} accent="bg-status-info" testid="kpi-tile-ontrack" />
        <SummaryCard label="Open anomalies"
          value={summary.open_anomalies}
          sub={`${summary.anomalies_high} high · ${summary.anomalies_medium} med · ${summary.anomalies_low} low`}
          icon={AlertTriangle} accent="bg-status-error" testid="kpi-tile-anomalies" />
        <SummaryCard label="Report readiness"
          value={`${summary.report_readiness}%`}
          sub="composite of progress + confidence" icon={FileBarChart2} accent="bg-status-warning" testid="kpi-tile-readiness" />
      </div>

      {/* Emissions chart + featured KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Emissions trend */}
        <div className="card p-4 lg:col-span-7" data-testid="emissions-trend-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="section-label">GHG Emissions Trend (Scope 1 + 2 + 3)</div>
              <div className="text-sm text-ink-soft mt-1">Quarterly tCO₂e · stacked by scope</div>
            </div>
            <div className="flex gap-3 text-[11px]">
              <span className="flex items-center gap-1"><span className="dot" style={{background:'#064E3B'}}/> Scope 1</span>
              <span className="flex items-center gap-1"><span className="dot" style={{background:'#10B981'}}/> Scope 2</span>
              <span className="flex items-center gap-1"><span className="dot" style={{background:'#A7F3D0'}}/> Scope 3</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 border-t-2 border-status-error"/> Total S1+2+3</span>
            </div>
          </div>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={emissions_trend} barCategoryGap={14}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={50} />
                <Tooltip />
                <Bar dataKey="scope1" stackId="a" fill="#064E3B" radius={[2,2,0,0]} />
                <Bar dataKey="scope2" stackId="a" fill="#10B981" />
                <Bar dataKey="scope3" stackId="a" fill="#A7F3D0" radius={[0,0,2,2]} />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#EF4444"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#EF4444", stroke: "#EF4444" }}
                  activeDot={{ r: 5 }}
                  name="Total S1+2+3"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Featured KPI cards */}
        <div className="card p-4 lg:col-span-5">
          <div className="flex items-center justify-between">
            <div className="section-label">High-Signal KPIs</div>
            <Link to="/scorecard" className="text-xs text-brand font-semibold flex items-center gap-1">
              See all <ChevronRight size={12}/>
            </Link>
          </div>
          <div className="mt-3 divide-y divide-border" data-testid="featured-kpis">
            {featured.map((r) => {
              const color = ISSUE_COLORS[r.kpi.material_issue] || "#064E3B";
              const trend = r.improving ? <TrendingDown size={14} className="text-status-success"/> : <TrendingUp size={14} className="text-status-error"/>;
              return (
                <Link
                  key={r.kpi.id}
                  to={`/kpi/${r.kpi.id}`}
                  className="py-2 flex items-center gap-3 hover:bg-surface px-1 rounded-sm"
                  data-testid={`featured-${r.kpi.id}`}
                >
                  <span className="dot" style={{background:color}}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.kpi.name}</div>
                    <div className="text-[11px] text-ink-soft">{r.kpi.material_issue}</div>
                  </div>
                  <div className="w-24"><MiniSpark data={r.spark.map(v=>({v}))} color={color}/></div>
                  <div className="text-right w-28">
                    <div className="font-mono text-sm">{fmtNumber(r.current_value)} <span className="text-[11px] text-ink-soft">{r.kpi.unit}</span></div>
                    <div className="text-[11px] text-ink-soft flex items-center justify-end gap-1">
                      {trend}{fmtPct(r.qoq_change_pct)}
                    </div>
                  </div>
                  {r.open_anomalies > 0 && (
                    <span className="pill pill-error" title="open anomalies">{r.open_anomalies}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Material issues grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="section-label">Material Issues</div>
          <div className="text-xs text-ink-soft flex items-center gap-1"><Sparkles size={12}/> Click any tile to drill into the issue's KPIs.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(byIssue).map(([issue, rows]) => (
            <MaterialIssueCard key={issue} issue={issue} rows={rows}/>
          ))}
        </div>
      </div>
    </div>
  );
}
